import { FC, useMemo } from 'react';

import { Transaction } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { useTranslations } from '@ravago/shared/translations/client';

import { canSelectInvoice } from '../../../../../utils/invoice.utils';

import { OpenInvoiceTableRow } from './open-invoice-table-row';

type Props = {
   currency: string;
   invoices: Transaction[];
   canPayOnline: boolean;
   selectedInvoiceIds: number[];
   onInvoiceSelectChange: (invoiceId: number, selected: boolean) => void;
   onSelectAllChange: (invoiceIds: number[], selected: boolean) => void;
};

export const OpenInvoiceTableDesktop: FC<Props> = ({
   currency,
   invoices,
   canPayOnline,
   selectedInvoiceIds,
   onInvoiceSelectChange,
   onSelectAllChange
}) => {
   const translate = useTranslations();

   const selectableInvoices = invoices.filter(canSelectInvoice);

   const handleSelectAllInvoices = (selected: boolean) => {
      onSelectAllChange(
         selectableInvoices.map(({ id }) => id),
         selected
      );
   };

   const handleInvoiceSelectChange = (invoiceId: number, selected: boolean) => {
      onInvoiceSelectChange(invoiceId, selected);
   };

   const { checked, indeterminate } = useMemo(() => {
      const containsAll =
         selectableInvoices.length > 0 && selectableInvoices.every((selectableInvoice) => selectedInvoiceIds.includes(selectableInvoice.id));
      return {
         checked: containsAll,
         indeterminate: !containsAll && selectableInvoices.some((selectableInvoice) => selectedInvoiceIds.includes(selectableInvoice.id))
      };
   }, [selectableInvoices, selectedInvoiceIds]);
   const canSelectAll = selectableInvoices.length > 0;

   return (
      <Box direction="column">
         <Table>
            <TableHead>
               <TableRow>
                  {canPayOnline && (
                     <TableHeader>
                        <Checkbox disabled={!canSelectAll} checked={checked} indeterminate={indeterminate} onChange={handleSelectAllInvoices} />
                     </TableHeader>
                  )}
                  <TableHeader>{translate('invoices.open-invoices.table.table-header.invoice')}</TableHeader>
                  <TableHeader>{translate('invoices.open-invoices.table.table-header.date')}</TableHeader>
                  <TableHeader>{translate('invoices.open-invoices.table.table-header.due-date')}</TableHeader>
                  <TableHeader align="right">{translate('invoices.open-invoices.table.table-header.open-amount', { currency })}</TableHeader>
                  <TableHeader>{/* Placeholder */}</TableHeader>
                  <TableHeader>{/* Placeholder */}</TableHeader>
               </TableRow>
            </TableHead>
            <TableBody>
               {invoices.map((invoice, index) => (
                  <OpenInvoiceTableRow
                     // eslint-disable-next-line react/no-array-index-key
                     key={index}
                     invoice={invoice}
                     canPayOnline={canPayOnline}
                     selected={selectedInvoiceIds.includes(invoice.id)}
                     onSelectChange={(selected) => handleInvoiceSelectChange(invoice.id, selected)}
                  />
               ))}
            </TableBody>
         </Table>
      </Box>
   );
};
